import {Component, inject, signal, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {DialogComponent} from '@components/dialogs/dialog/dialog.component';
import {IconCheckedCircle} from '@components/icons/icon-checked-circle/icon-checked-circle.component';
import {IconPencilComponent} from '@components/icons/icon-pencil/icon-pencil.component';
import {Anexo} from '@core/models/anexo';
import {CadastrarDemandaPayload} from '@core/models/demanda';
import {AdicionarDemandaStateService} from '@core/services/adicionar-demanda-state.service';
import {AuthService} from '@core/services/auth.service';
import {DemandaService} from '@core/services/demanda.service';
import {
  DadosLocalizacaoDemandaComponent
} from '@pages/demandas/dados-localizacao-demanda/dados-localizacao-demanda.component';
import {FormAnexosDemandaComponent} from '@pages/demandas/form-anexos-demanda/form-anexos-demanda.component';
import {FormDescricaoDemandaComponent} from '@pages/demandas/form-descricao-demanda/form-descricao-demanda.component';
import {ID_REPRESENTACAO_KEY} from '@shared/constants/storage-keys';
import {MessageService} from 'primeng/api';
import {Button} from 'primeng/button';
import {Card} from 'primeng/card';
import {IconBoldRam2Component} from '@components/icons/icon-bold-ram-2/icon-bold-ram-2.component';
import {IconBoldArrowLeftComponent} from '@components/icons/icon-bold-arrow-left/icon-bold-arrow-left.component';

@Component({
  selector: 'app-adicionar-demanda-anexos',
  imports: [
    Card,
    Button,
    DadosLocalizacaoDemandaComponent,
    IconCheckedCircle,
    IconPencilComponent,
    FormAnexosDemandaComponent,
    DialogComponent,
    FormDescricaoDemandaComponent,
    ReactiveFormsModule,
    IconBoldRam2Component,
    IconBoldArrowLeftComponent,
  ],
  templateUrl: './adicionar-demanda-anexos.component.html',
  styleUrl: './adicionar-demanda-anexos.component.css',
})
export class AdicionarDemandaAnexosComponent {
  @ViewChild(FormAnexosDemandaComponent) private readonly formAnexos?: FormAnexosDemandaComponent;

  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly demandaService = inject(DemandaService);
  private readonly authService = inject(AuthService);
  protected descricaoForm!: FormGroup;
  protected readonly state = inject(AdicionarDemandaStateService);
  protected readonly uploadEmAndamento = signal(false);
  protected submitting = false;

  protected readonly showConfirmarDialog = signal(false);
  private readonly fb = inject(FormBuilder);

  protected onAnexosChange(anexos: Anexo[]): void {
    this.state.setAnexos(anexos);
  }

  protected onCriarDemanda(): void {
    this.formAnexos?.markAllAsTouched();
    if (this.formAnexos && !this.formAnexos.isValid()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção!',
        detail: 'Preencha todos os campos obrigatórios.'
      });
      return;
    }
    this.initDescricaoForm();
    this.showConfirmarDialog.set(true);
  }

  protected onUploadStatusChange(inProgress: boolean): void {
    this.uploadEmAndamento.set(inProgress);
  }

  protected onVoltar(): void {
    this.state.irParaStep(1);
    this.router.navigate(['/demandas/adicionar/descricao']);
  }

  protected onEditLocalizacao(): void {
    this.state.limparLocalizacao();
    this.state.irParaStep(0);
    this.router.navigate(['/demandas/adicionar']);
  }

  protected onConfirmarSubmit(): void {
    const localizacao = this.state.dadosLocalizacao();
    const descricao = this.state.descricaoDemanda();
    const tipoObraId = this.state.tipoObraId();
    const isConcessionaria = this.authService.isAuthenticatedConcessionaria();

    const payload: CadastrarDemandaPayload = {
      dadosLocalizacao: {
        longitude: localizacao?.longitude ?? null,
        latitude: localizacao?.latitude ?? null,
        municipio: localizacao?.municipio ?? null,
        codRodovia: localizacao?.codRodovia ?? null,
        concessionaria: localizacao?.concessionaria ?? null,
      },
      dadosDescricao: {
        idRepresentacao: JSON.parse(sessionStorage.getItem(ID_REPRESENTACAO_KEY) ?? 'null') as number | null,
        idTipoRepresentacao: isConcessionaria ? null : (descricao?.tipoRepresentacao?.id ?? null),
        nomeSolicitante: descricao?.nomeSolicitante ?? null,
        justificativa: descricao?.justificativa ?? null,
        numeroProcessoSei: descricao?.numeroProcessoSei ?? null,
        possuiNumeroSei: descricao?.existeProcessoSei ?? null,
        idTipoProcesso: descricao?.tipoProcessoSei?.id ?? null,
        descricaoProcesso: descricao?.descricaoProcesso ?? null,
        representacaoUsuarioConcessionaria: isConcessionaria ? (descricao?.representacaoUsuarioConcessionaria ?? null) : null,
      },
      idTipoObra: tipoObraId,
      idSentidoRodovia: null,
      infoArquivos: this.state.anexos().map((anexo) => ({
        idArquivo: anexo.idArquivo,
        idTipoAnexo: anexo.idTipoAnexo,
        nomeOriginal: anexo.nomeOriginal,
        nomeUnicoIdentificador: anexo.nomeUnicoIdentificador,
        descricaoOutros: anexo.descricaoOutros,
        extensao: anexo.extensao,
        tamanho: anexo.tamanho,
      })),
    };

    this.submitting = true;
    this.demandaService.cadastrarDemanda(payload).subscribe({
      next: () => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso!',
          detail: 'Demanda criada com sucesso.',
        });
        this.showConfirmarDialog.set(false);
        setTimeout(() => this.router.navigate(['/']), 1000);
      },
      error: () => {
        this.submitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erro!',
          detail: 'Falha ao criar demanda.',
        });
        this.showConfirmarDialog.set(false);
      },
    });
  }

  private initDescricaoForm(): void {
    const desc = this.state.descricaoDemanda();
    this.descricaoForm = this.fb.group({
      tipoRepresentacao: [{value: desc?.tipoRepresentacao ?? null, disabled: true}],
      nomeSolicitante: [{value: desc?.nomeSolicitante ?? '', disabled: true}],
      justificativa: [{value: desc?.justificativa ?? '', disabled: true}],
      existeProcessoSei: [{value: desc?.existeProcessoSei ?? false, disabled: true}],
      numeroProcessoSei: [{value: desc?.numeroProcessoSei ?? null, disabled: true}],
      tipoProcessoSei: [{value: desc?.tipoProcessoSei ?? null, disabled: true}],
      descricaoProcesso: [{value: desc?.descricaoProcesso ?? null, disabled: true}],
      representacaoUsuarioConcessionaria: [{value: desc?.representacaoUsuarioConcessionaria ?? null, disabled: true}],
    });
  }
}
